import SeoHead from '@/components/base/SeoHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroBanner from './components/HeroBanner';
import CategoryGrid from './components/CategoryGrid';
import FeaturedArticles from './components/FeaturedArticles';
import NewsletterSection from './components/NewsletterSection';

export default function Home() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '长日光阴',
        url: 'https://gxjiaoyan.cn',
        description: '用文字、影像与生活美学，帮你重新感受时间的重量，找回被快节奏吞噬的从容与诗意。',
        sameAs: ['https://weibo.com/changriguangyin', 'https://www.xiaohongshu.com/changriguangyin'],
      },
      {
        '@type': 'WebSite',
        name: '长日光阴',
        url: 'https://gxjiaoyan.cn',
        description: '长日光阴——珍藏每一寸慢时光。慢生活美学品牌，专注于生活美学、慢生活、时光记录、治愈系生活内容。',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://gxjiaoyan.cn/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '首页',
        url: 'https://gxjiaoyan.cn',
        isPartOf: { '@id': 'https://gxjiaoyan.cn/#website' },
      },
    ],
  };

  return (
    <>
      <SeoHead
        title="首页"
        description="长日光阴——珍藏每一寸慢时光。用文字、影像与生活美学，帮你重新感受时间的重量，找回被快节奏吞噬的从容与诗意。探索慢生活、时光记录、生活美学、岁月感、治愈系生活、慢时光。"
        keywords="长日光阴,慢生活,时光记录,生活美学,岁月感,治愈系生活,慢时光"
        canonicalUrl="https://gxjiaoyan.cn"
        ogType="website"
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main>
        <HeroBanner />

        <article aria-label="品牌理念" className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed font-heading">
              「长日光阴」是对生命中每一个悠长白昼的温柔致敬。<br />
              我们相信，<strong>慢不是效率的反面，而是深度的前提</strong>。<br />
              在这里，没有KPI和截止日期，只有一杯茶的时间、一页书的厚度，<br />
              和一段值得被好好记住的时光。
            </p>
          </div>
        </article>

        <CategoryGrid />
        <FeaturedArticles />

        <article className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-xl md:text-2xl font-heading text-foreground-800 italic leading-relaxed mb-6">
              "从前的日色变得慢，<br className="sm:hidden" />车、马、邮件都慢，<br />一生只够爱一个人。"
            </blockquote>
            <cite className="text-sm text-foreground-400 not-italic">—— 木心 《从前慢》</cite>
          </div>
        </article>

        <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
                让每一天都值得被记住
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto leading-relaxed">
                长日光阴不是一个品牌，而是一种生活方式的邀请。<br />
                邀请你在匆忙的世界里，为自己留一扇可以慢下来的窗。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-pen-nib-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">深度文字</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">每一篇文章都经过精心打磨，拒绝碎片化阅读，让文字重新拥有重量。</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-camera-lens-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">影像美学</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">用镜头捕捉日常中的诗意，让每一帧画面都有温度和故事。</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-heart-line text-2xl text-secondary-700"></i>
                </div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">治愈陪伴</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">在疲惫的时刻，给你一隅可以放松的角落，让心灵得到真正的休憩。</p>
              </div>
            </div>
          </div>
        </section>

        <NewsletterSection />
      </main>

      <Footer />
    </>
  );
}